import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useFetch, formatDate } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { Eyebrow, Reveal } from "@/components/site/Motion";

export default function Insights() {
  const { data: posts } = useFetch("/posts");

  useSEO({
    title: "Insights — Notes on Process & Execution | RenB Solutions",
    description: "Essays from RenB on process debt, delegation, strategy execution and operating rhythms for SME founders and CEOs.",
    path: "/insights",
  });

  return (
    <div data-testid="insights-page">
      <section className="border-b border-ash">
        <div className="mx-auto max-w-[1440px] px-5 pb-16 pt-28 md:px-10 md:pb-24 md:pt-40">
          <Reveal>
            <Eyebrow>Insights</Eyebrow>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              Notes on process, execution &amp; the founder&apos;s calendar<span className="text-progress">.</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        {(posts || []).length === 0 && (
          <div className="border border-ash p-16 text-center">
            <p className="font-serif text-3xl">No posts yet.</p>
            <p className="mt-3 text-sm text-slategray">New essays are on the way.</p>
          </div>
        )}
        {(posts || []).map((p, i) => (
          <Reveal key={p.id} delay={Math.min(i * 0.05, 0.15)}>
            <Link
              to={`/insights/${p.slug}`}
              data-testid={`post-row-${p.slug}`}
              className="group grid gap-4 border-t border-ash py-10 transition-colors duration-300 hover:bg-ash/30 md:grid-cols-12 md:gap-8 md:py-14"
            >
              <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-slategray md:col-span-3">
                {formatDate(p.published_at)}
              </p>
              <div className="md:col-span-8">
                <h2 className="max-w-3xl font-serif text-3xl font-medium leading-tight tracking-tight transition-colors group-hover:text-progress md:text-5xl">
                  {p.title}
                </h2>
                <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-obsidian/65">{p.excerpt}</p>
                {p.tags && (
                  <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-progress">{p.tags}</p>
                )}
              </div>
              <div className="flex items-start justify-end md:col-span-1">
                <span className="inline-flex h-11 w-11 items-center justify-center border border-obsidian/15 transition-all duration-300 group-hover:border-progress group-hover:bg-progress group-hover:text-white">
                  <ArrowUpRight size={17} />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
        <div className="border-t border-ash" />
      </section>
    </div>
  );
}
